import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {map, Observable} from 'rxjs';

import {ProjectFileControllerService} from '../../../../api/api/projectFileController.service';
import {
  DocumentClass,
  DocumentClassFormData,
  DocumentClassListResponse,
  DocumentClassReclassifyStartResponse,
} from './document-class.models';

/**
 * Service für die Dokumentenklassenverwaltung.
 *
 * Kapselt die REST-Aufrufe gegen die Backend-Endpunkte unter
 * `projects/{projectId}/document-classes`. Die Basis-URL wird – analog zu den übrigen
 * Feature-Services – aus einem generierten API-Service abgeleitet, damit Dev-Proxy und
 * Produktivumgebung identisch aufgelöst werden.
 *
 * Hinweis: Sobald der passende API-Client generiert wurde, kann dieser HttpClient-Wrapper
 * durch den generierten Controller-Service ersetzt werden.
 */
@Injectable({providedIn: 'root'})
export class DocumentClassService {
  private http = inject(HttpClient);
  private projectFileApi = inject(ProjectFileControllerService);

  private get basePath(): string {
    return (this.projectFileApi as any).basePath ?? '';
  }

  /** Lädt alle Dokumentenklassen eines Projekts inkl. Reklassifizierungs-Hinweis. */
  list(projectId: number): Observable<DocumentClassListResponse> {
    return this.http.get<DocumentClass[] | DocumentClassListResponse>(this.classesUrl(projectId)).pipe(
      map(response => this.normalizeListResponse(response)),
    );
  }

  /** Legt eine neue Dokumentenklasse an. */
  create(projectId: number, data: DocumentClassFormData): Observable<DocumentClass> {
    return this.http.post<DocumentClass>(this.classesUrl(projectId), this.normalize(data));
  }

  /** Aktualisiert eine bestehende Dokumentenklasse. */
  update(projectId: number, id: string, data: DocumentClassFormData): Observable<DocumentClass> {
    return this.http.put<DocumentClass>(`${this.classesUrl(projectId)}/${id}`, this.normalize(data));
  }

  /** Löscht eine Dokumentenklasse. Zugeordnete Dateien werden der Default-Klasse zugeordnet. */
  delete(projectId: number, id: string): Observable<void> {
    return this.http.delete<void>(`${this.classesUrl(projectId)}/${id}`);
  }

  /**
   * Stößt die erneute Klassifizierung an – beschränkt auf Dateien der geänderten/neuen
   * Klassen bzw. der Default-Klasse "Sonstige Dokumente".
   */
  reclassify(projectId: number): Observable<DocumentClassReclassifyStartResponse> {
    return this.http.post<DocumentClassReclassifyStartResponse>(`${this.classesUrl(projectId)}/reclassify`, {});
  }

  /** Stößt die KI-Klassifizierung einer einzelnen Datei an. */
  reclassifyFile(projectId: number, fileId: string): Observable<void> {
    return this.http.post<void>(`${this.classesUrl(projectId)}/reclassify-file/${fileId}`, {});
  }

  private classesUrl(projectId: number): string {
    return `${this.basePath}/projects/${projectId}/document-classes`;
  }

  /** Stellt sicher, dass Dateiformate nur bei aktivierter Format-Zuordnung übermittelt werden. */
  private normalize(data: DocumentClassFormData): DocumentClassFormData {
    return {
      title: data.title.trim(),
      description: data.description.trim(),
      assignByFileFormat: data.assignByFileFormat,
      fileFormats: data.assignByFileFormat ? data.fileFormats : [],
    };
  }

  private normalizeListResponse(
    response: DocumentClass[] | DocumentClassListResponse,
  ): DocumentClassListResponse {
    if (Array.isArray(response)) {
      return {
        classes: response,
        needsReclassification: response.some(documentClass => documentClass.needsReclassification),
      };
    }

    return {
      classes: response.classes ?? [],
      needsReclassification: response.needsReclassification ?? false,
      unclassifiedFileCount: response.unclassifiedFileCount ?? 0,
    };
  }
}
